import {Injectable, InjectionToken} from '@angular/core';
import {WindowWrapper} from './window.injectable';
import {UserProfile} from '../shared/models';
import {select} from '@angular-redux/store';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment.local';

export const AnalyticsServiceToken =
  new InjectionToken<AnalyticsService>('Google Analytics Service');

export interface AnalyticsService {
  setupGoogleAnalyticsAndTagManagerScripts(userTrackingKey: string): void;

  setupGTMScript(window: any, userTrackingKey: string): void;

  setupGoogleAnalyticsScript(window: any): void;

  sendPageView(url: string): void;

  sendFetchUser(userTrackingKey: string, jobTitle: string): void;

  setMemberDescription(memberDescription: string): void;

  handleGoogleAnalytics(category: string, eventLabel: string, eventAction: string): void;
}

@Injectable()
export class GoogleAnalyticsService implements AnalyticsService {

  private readonly isStage: boolean;

  private readonly CUSTOM_DIMENSIONS: { [index: string]: string };

  @select(['data', 'userProfile'])
  private readonly userProfile$: Observable<UserProfile>;

  constructor(private _window: WindowWrapper) {
    this.isStage = environment.api.includes('stage');
    this.CUSTOM_DIMENSIONS = {
      loginId: 'dimension1',
      companyId: this.isStage ? 'dimension2' : 'dimension7',
      jobTitle: 'dimension5'
    };
  }

  setupGoogleAnalyticsAndTagManagerScripts(userTrackingKey: string) {
    const window: any = this._window.nativeWindow;
    this.setupGTMScript(window, userTrackingKey);
    this.setupGoogleAnalyticsScript(window);
  }

  setupGTMScript(window: any, userTrackingKey: string) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      {login: userTrackingKey},
      {'gtm.start': new Date().getTime(), event: 'gtm.js'});
    const f = document.getElementsByTagName('script')[0],
      j = document.createElement('script');

    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + environment.GTMContainerId + '&l=dataLayer&cx=c';
    f?.parentNode?.insertBefore(j, f);
  }

  setupGoogleAnalyticsScript(window: any) {
    window.ga = window.ga || function () {
      (
        window.ga.q = window.ga.q || []
      ).push(arguments);
    };
    window.ga.l = +new Date;
    window.ga('create', environment.googleAnalyticsId, 'auto');
  }

  sendPageView(url: string): void {
    this._window.nativeWindow.ga('set', 'page', url);
    this._window.nativeWindow.ga('send', 'pageview');
  }

  sendFetchUser(userTrackingKey: string, jobTitle: string) {
    this._window.nativeWindow.ga('set', this.CUSTOM_DIMENSIONS.loginId, userTrackingKey);
    this._window.nativeWindow.ga('set', this.CUSTOM_DIMENSIONS.jobTitle, jobTitle);
    this._window.nativeWindow.ga('set', 'userId', userTrackingKey);
    this._window.nativeWindow.ga('set', 'title', jobTitle);
  }

  setMemberDescription(memberDescription: string): void {
    this._window.nativeWindow.ga('set', this.CUSTOM_DIMENSIONS.companyId, memberDescription);
  }

  handleGoogleAnalytics(category: string, eventLabel: string, eventAction: string): void {
    this._window.nativeWindow.ga(
      'send',
      'event',
      {
        eventCategory: category,
        eventAction: eventAction,
        eventLabel: eventLabel
      }
    );
  }
}

export const MONTH_MAPPING: { [index: string]: string } = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December'
};

export function formatDate(month: number, year: number): string {
  return `${MONTH_MAPPING[month]} ${year}`;
}
